import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Select, DatePicker, Popover, Row, Col } from 'antd';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../actions';
import * as selectors from '../../selectors';
import { AUDIT_ACTION_LABELS, AUDIT_ACTION_OPTIONS } from '../../constants';
import {
    components as Core,
    translate,
    constants as coreConstants,
    getTableProps,
    getPagination,
    valueBuilder
} from '../../../core';

const { RangePicker } = DatePicker;

export const COLUMNS = {
    date: 'date',
    admin: 'admin',
    action: 'action',
    target: 'target',
    changes: 'changes',
    ip: 'ip'
};

class AuditTrail extends React.PureComponent {
    static propTypes = {
        i18n: PropTypes.object.isRequired,
        actions: PropTypes.object.isRequired,
        logs: PropTypes.array,
        admins: PropTypes.array,
        isLoading: PropTypes.bool,
        pagination: PropTypes.object.isRequired
    };

    static defaultProps = {
        logs: [],
        admins: [],
        isLoading: false
    };

    constructor(props) {
        super(props);
        const { i18n } = this.props;
        this.state = { action: undefined, adminId: undefined, from: undefined, to: undefined };
        this.tableColumns = [{
            title: i18n.t('auditTrail.date'),
            key: COLUMNS.date,
            dataIndex: COLUMNS.date,
            render: valueBuilder.getLocalFullTime,
            sorter: true,
            sortDirections: coreConstants.BOTH_SORT_DIRECTIONS
        }, {
            title: i18n.t('auditTrail.admin'),
            key: COLUMNS.admin,
            render: this.renderAdmin
        }, {
            title: i18n.t('auditTrail.action'),
            key: COLUMNS.action,
            dataIndex: 'action',
            render: this.renderAction
        }, {
            title: i18n.t('auditTrail.target'),
            key: COLUMNS.target,
            render: this.renderTarget
        }, {
            title: i18n.t('auditTrail.changes'),
            key: COLUMNS.changes,
            dataIndex: 'changes',
            render: this.renderChanges
        }, {
            title: i18n.t('auditTrail.ip'),
            key: COLUMNS.ip,
            dataIndex: 'ip'
        }];
        this.fetch();
        this.props.actions.getAuditAdmins();
    }

    fetch = (tableProps = {}) => {
        const { action, adminId, from, to } = this.state;
        this.props.actions.getAuditLogs({ ...tableProps, actionType: action, adminId, from, to });
    };

    onChangeTable = (pagination, filters, sorter) => {
        this.fetch(getTableProps(pagination, filters, sorter));
    };

    onActionChange = action => {
        this.setState({ action }, () => this.fetch({ page: 1 }));
    };

    onAdminChange = adminId => {
        this.setState({ adminId }, () => this.fetch({ page: 1 }));
    };

    filterAdminOption = (input, option) => String(option.props.children).toLowerCase().includes(input.toLowerCase());

    onDateChange = range => {
        const [from, to] = range || [];
        this.setState({
            from: from ? from.startOf('day').toISOString() : undefined,
            to: to ? to.endOf('day').toISOString() : undefined
        }, () => this.fetch({ page: 1 }));
    };

    renderAdmin = record => record.adminName || record.adminEmail || record.adminId;

    renderAction = action => AUDIT_ACTION_LABELS[action] || action;

    renderTarget = record => {
        if (!record.targetLabel && !record.targetType) return '—';
        return record.targetType
            ? `${record.targetLabel || record.targetId || ''} (${record.targetType})`
            : record.targetLabel;
    };

    renderChanges = changes => {
        const parsed = this.parseChanges(changes);
        if (!parsed.length) return '—';
        const content = (
            <div>
                {parsed.map((c, i) => (
                    <div key={i}>
                        <b>{c.Field || c.field}</b>: {this.displayValue(c.OldValue !== undefined ? c.OldValue : c.oldValue)}
                        {' → '}
                        {this.displayValue(c.NewValue !== undefined ? c.NewValue : c.newValue)}
                    </div>
                ))}
            </div>
        );
        const { i18n } = this.props;
        return (
            <Popover content={content} title={i18n.t('auditTrail.changes')} trigger="click">
                <a>{i18n.t('auditTrail.changesCount', { count: parsed.length })}</a>
            </Popover>
        );
    };

    displayValue = value => (value === null || value === undefined || value === '' ? '∅' : String(value));

    parseChanges = changes => {
        if (!changes) return [];
        try {
            const parsed = JSON.parse(changes);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    };

    get pagination() {
        return getPagination(this.props.pagination);
    }

    get customFilters() {
        const { i18n, admins } = this.props;
        return (
            <Col sm={24} md={20} xl={18}>
                <Row type="flex" gutter={12}>
                    <Col>
                        <Select
                            allowClear
                            showSearch
                            style={{ minWidth: 220 }}
                            placeholder={i18n.t('auditTrail.filterAdmin')}
                            value={this.state.adminId}
                            filterOption={this.filterAdminOption}
                            onChange={this.onAdminChange}>
                            {admins.map(a => (
                                <Select.Option key={a.id} value={a.id}>{a.name || a.email}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <Select
                            allowClear
                            style={{ minWidth: 220 }}
                            placeholder={i18n.t('auditTrail.filterAction')}
                            value={this.state.action}
                            onChange={this.onActionChange}>
                            {AUDIT_ACTION_OPTIONS.map(o => (
                                <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col>
                        <RangePicker onChange={this.onDateChange} />
                    </Col>
                </Row>
            </Col>
        );
    }

    render() {
        const { isLoading, logs, i18n } = this.props;
        return (
            <div>
                <p>{i18n.t('screens.auditTrail')}</p>
                <Core.Table
                    columns={this.tableColumns}
                    data={logs}
                    isLoading={isLoading}
                    pagination={this.pagination}
                    onChange={this.onChangeTable}
                    hasCustomFilters
                    customFilters={this.customFilters}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    logs: selectors.getAuditLogs(state),
    admins: selectors.getAuditAdmins(state),
    pagination: selectors.getAuditLogsPagination(state),
    isLoading: selectors.isGettingAuditLogs(state)
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(actions, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(translate()(AuditTrail));
